/**
 * ScandiPWA - Progressive Web App for Magento
 *
 * Copyright © Scandiweb, Inc. All rights reserved.
 * See LICENSE for license details.
 *
 * @license OSL-3.0 (Open Software License ("OSL") v. 3.0)
 * @package scandipwa/base-theme
 * @link https://github.com/scandipwa/base-theme
 */

import PropTypes from 'prop-types';
import { PureComponent, createRef } from 'react';

import Html from 'Component/Html';
import Image from 'Component/Image';
import Link from 'Component/Link';
import media, { WYSIWYG_MEDIA } from 'Util/Media/Media';

import { getPostUrl } from '../../../../util/Posts';

import './NewEventsBlogPostCard.style';

/** @namespace ScandiPWA/Blog/Component/BlogPostCard/NewEventsBlogPostCard/Component */
export class NewEventsBlogPostCard extends PureComponent {
    static propTypes = {
        post: PropTypes.oneOfType([
            PropTypes.object,
            PropTypes.bool
        ]),
        isPlaceholder: PropTypes.bool,
        isCompactOnMobile: PropTypes.bool
    };

    static defaultProps = {
        post: false,
        isPlaceholder: false,
        isCompactOnMobile: false
    };

    state = {
        isShowMoreExpanded: false,
        isOverflowing: false
    }

    contentRef = createRef();

    monthMap = {
        '01': __('January'),
        '02': __('Febrary'),
        '03': __('March'),
        '04': __('April'),
        '05': __('May'),
        '06': __('June'),
        '07': __('July'),
        '08': __('August'),
        '09': __('September'),
        '10': __('October'),
        '11': __('November'),
        '12': __('December')
    };

    componentDidMount() {
        const element = this.contentRef;

        // SNIPPET HELPER INFO FOR REF
        // Using timeout to check ref status on mount because ref is async and doesn't actually has a referance to the dom element on mount since the render of the component
        // is based on condition. To overcome this issue we need a timeout for the dom to actually write the elements and await the creation of the referance to calculate its height on "mount" 
        setTimeout(
            () => {
                if (element.current?.scrollHeight > element.current?.offsetHeight) {
                    this.setState({ isOverflowing: true });
                }
            }, 200)
    }

    showMoreToggle() {
        const { isShowMoreExpanded } = this.state;
        return this.setState({ isShowMoreExpanded: !isShowMoreExpanded });
    }

    renderImage() {
        const {
            post: {
                first_image,
                media_gallery,
                title
            },
            topFirstMagazine,
            latestMagazine,
            post
        } = this.props;

        if (!topFirstMagazine && latestMagazine && media_gallery) {
            const mediaGalleryArray = media_gallery.split(';');

            if (mediaGalleryArray[0]) {
                return (
                    <div
                        mix={{ block: 'NewEventsBlogPostCard', elem: 'ImageWrapper' }}
                    >
                        <Image
                            mix={{ block: 'NewEventsBlogPostCard', elem: 'Image' }}
                            ratio="square"
                            src={media(mediaGalleryArray[0])}
                        />
                    </div>
                );
            }
        }

        if (!first_image) {
            return null;
        }

        return (
            <Link
                to={getPostUrl(post)}
                title={__('View %s', title)}
                mix={{ block: 'NewEventsBlogPostCard', elem: 'ImageWrapper' }}
            >
                <Image
                    mix={{ block: 'NewEventsBlogPostCard', elem: 'Image' }}
                    ratio="square"
                    src={first_image}
                />
            </Link>
        );
    }

    renderTitle() {
        const { post: { title }, post } = this.props;
        if (!title) {
            return null;
        }

        return (
            <Link to={getPostUrl(post)}>
                {title}
            </Link>
        );
    }

    renderDescription() {
        const { post: { short_content } } = this.props;

        if (!short_content) {
            return null;
        }

        return <Html content={short_content} />
    }
    renderDate(date) {
        if (!date) {
            return null;
        }

        const dateObj = new Date(date);

        return `${dateObj.getDate()} ${this.monthMap[dateObj.getMonth() + 1 < 10 ? `0${dateObj.getMonth() + 1}` : dateObj.getMonth() + 1]} ${dateObj.getFullYear()}`;
    }

    renderContents() {
        const { isPlaceholder, post, post: { publish_time, event_start_date = null, event_end_date = null } } = this.props;
        const { isOverflowing, isShowMoreExpanded } = this.state;
        if (isPlaceholder) {
            return null;
        }

        return (
            <div block="NewEventsBlogPostCard" elem="ContentWrapper" mods={{ expand: isShowMoreExpanded }}>
                {this.renderImage()}
                <div block="NewEventsBlogPostCard" elem="Details">
                    {/* <p block="NewEventsBlogPostCard" elem="Content_Event"> */}
                    {/* {__('ΤΕΥΧΟΣ')} */}
                    {/* </p> */}
                    <div block="NewEventsBlogPostCard" elem="Content_Title">
                        {this.renderTitle()}
                    </div>
                    <p block="NewEventsBlogPostCard" elem="Content_Date">
                        {event_start_date ?
                            <>
                                {this.renderDate(event_start_date)}
                            </>
                            :
                            <>
                                {!event_end_date && this.renderDate(publish_time)}
                            </>
                        }
                        {event_end_date &&
                            <>
                                {event_start_date && ` - `}
                                {this.renderDate(event_end_date)}
                            </>
                        }
                    </p>
                    <div key={`id=${post.post_id}`} ref={this.contentRef} block="NewEventsBlogPostCard" elem="Content_Details" style={isShowMoreExpanded ? { height: "100%" } : { height: "156px" }}>
                        {this.renderDescription()}
                    </div>
                    <div block="NewEventsBlogPostCard" elem="Content_Actions">
                        {isOverflowing && <a className={'NewEvents_LatestPost_showMore'} onClick={() => this.showMoreToggle()}>{isShowMoreExpanded ? `- ${__('Show less')}` : `+ ${__('Show more')}`}</a>}
                        <Link to={getPostUrl(post)} className={'NewEvents_LatestPost_link Button'}>{__('See more')}</Link>
                    </div>
                </div>
            </div >
        );
    }


    render() {
        const { isPlaceholder, isCompactOnMobile, post: { publish_time }, normalGrid, selectedMonth } = this.props;
        if (!publish_time) return null;
        let month = publish_time.split('-')[1];

        if (normalGrid) {
            return (
                <>
                    <div className={"NewEventsBlogPostCard-month month-" + selectedMonth}>{this.monthMap[selectedMonth]}</div>
                    <div block={`NewEventsBlogPostCard month-${month}`} data-monthTile={this.monthMap[selectedMonth]} mods={{ isLoading: isPlaceholder, isCompactOnMobile }}>
                        {this.renderContents()}
                    </div>
                </>
            );
        }

        return (
            <div block={`NewEventsBlogPostCard month-${month}`} data-monthTile={this.monthMap[month]} mods={{ isLoading: isPlaceholder, isCompactOnMobile }}>
                {this.renderContents()}
            </div>
        );
    }

}

export default NewEventsBlogPostCard;
